import React, { useState, useEffect, useRef, useCallback } from "react";
import styles from "./Domov.module.css";
import Facebook from "../Logos/facebook.png";
import Youtube from "../Logos/youtube.png";
import rotarygloballogo from "../Logos/RotaryClubLogo.png";
import { useTranslation } from "react-i18next";
import AnimatedBackground from "../Layout/AnimatedBackground";

const IMAGES = [
  { src: "Slike/Domov/promo.jpg", alt: "Rotary Club Žalec" },
  { src: "Slike/Domov/slika1.jpg", alt: "Dobrodelni koncerti" },
  { src: "Slike/Domov/slika2.jpg", alt: "Predavanja" },
  { src: "Slike/Domov/slika3.jpg", alt: "Dobrodelne dražbe" },
  { src: "Slike/Domov/slika4.jpg", alt: "Sajenje dreves" },
];

const THUMBS = [
  {
    thumb: "Slike/Domov/promothumb.jpg",
    full: "Slike/Domov/promo.jpg",
    alt: "Rotary Club Žalec",
  },
  {
    thumb: "Slike/Domov/slika1thumb.jpg",
    full: "Slike/Domov/slika1.jpg",
    alt: "Dobrodelni koncerti",
  },
  {
    thumb: "Slike/Domov/slika2thumb.jpg",
    full: "Slike/Domov/slika2.jpg",
    alt: "Predavanja",
  },
  {
    thumb: "Slike/Domov/slika3thumb.jpg",
    full: "Slike/Domov/slika3.jpg",
    alt: "Dobrodelne dražbe",
  },
  {
    thumb: "Slike/Domov/slika4thumb.jpg",
    full: "Slike/Domov/slika4.jpg",
    alt: "Sajenje dreves",
  },
];

const SLIDE_MS = 5000;
const A11Y_TICK_MS = 250;

const cx = (...classNames) => classNames.filter(Boolean).join(" ");

const Domov = () => {
  const { t } = useTranslation();

  const [currentIndex, setCurrentIndex] = useState(0);

  const [isPlaying, setIsPlaying] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);
  const audioRef = useRef(null);
  const [isSpinning, setIsSpinning] = useState(false);

  const [visible, setVisible] = useState(false);
  const revealRef = useRef(null);

  const galleryRef = useRef(null);
  const glowRef = useRef(null);
  const imageRef = useRef(null);

  const touchStartX = useRef(null);
  const touchDeltaX = useRef(0);

  const [allowAnimate, setAllowAnimate] = useState(true);
  const galleryInView = useRef(true);
  const [pageVisible, setPageVisible] = useState(
    document.visibilityState === "visible"
  );

  const barRef = useRef(null);
  const barAnimRef = useRef(null);
  const slideTimerRef = useRef(null);
  const a11yTimerRef = useRef(null);
  const [progressNow, setProgressNow] = useState(0);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setAllowAnimate(!mq.matches);
    onChange();
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  useEffect(() => {
    const onVis = () => setPageVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  useEffect(() => {
    const onScroll = () => {
      if (!revealRef.current) return;
      const rect = revealRef.current.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.8) setVisible(true);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const io = new IntersectionObserver(
      ([entry]) => {
        galleryInView.current = !!entry?.isIntersecting;
      },
      { rootMargin: "0px", threshold: 0.35 }
    );

    if (galleryRef.current) io.observe(galleryRef.current);

    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio("/Slike/RotarySong.mp3");
    }

    const audio = audioRef.current;
    audio.preload = "metadata";
    audio.volume = 0.5;

    const onLoaded = () => setTotalDuration(Math.floor(audio.duration || 0));
    const onTime = () => setElapsedTime(Math.floor(audio.currentTime || 0));
    const onEnded = () => {
      setIsSpinning(false);
      setIsPlaying(false);
    };

    audio.addEventListener("loadedmetadata", onLoaded);
    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("ended", onEnded);

    return () => {
      audio.pause();
      audio.currentTime = 0;
      audio.removeEventListener("loadedmetadata", onLoaded);
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("ended", onEnded);
    };
  }, []);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsSpinning(false);
      setIsPlaying(false);
    } else {
      const p = audio.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
      setIsSpinning(true);
      setIsPlaying(true);
    }
  };

  const imgCount = IMAGES.length;

  const nextImage = useCallback(() => {
    setCurrentIndex((i) => (i + 1) % imgCount);
  }, [imgCount]);

  const prevImage = useCallback(() => {
    setCurrentIndex((i) => (i === 0 ? imgCount - 1 : i - 1));
  }, [imgCount]);

  useEffect(() => {
    if (!allowAnimate || document.visibilityState !== "visible") return;

    const nextIdx = (currentIndex + 1) % imgCount;
    const img = new Image();
    img.decoding = "async";
    img.loading = "eager";
    img.src = IMAGES[nextIdx].src;
  }, [currentIndex, allowAnimate, imgCount]);

  const clearSlideTimers = () => {
    if (slideTimerRef.current) {
      clearTimeout(slideTimerRef.current);
      slideTimerRef.current = null;
    }
    if (a11yTimerRef.current) {
      clearInterval(a11yTimerRef.current);
      a11yTimerRef.current = null;
    }
  };

  const startA11yTimer = () => {
    if (!barAnimRef.current) return;

    a11yTimerRef.current = setInterval(() => {
      const ct = barAnimRef.current?.currentTime || 0;
      setProgressNow(Math.min(100, Math.round((ct / SLIDE_MS) * 100)));
    }, A11Y_TICK_MS);
  };

  const animActive = allowAnimate && galleryInView.current && pageVisible;

  useEffect(() => {
    clearSlideTimers();
    setProgressNow(0);

    if (barAnimRef.current) {
      try {
        barAnimRef.current.cancel();
      } catch {}
      barAnimRef.current = null;
    }

    if (barRef.current) {
      barRef.current.style.transform = "scaleX(0)";
    }

    if (!barRef.current) return;

    const anim = barRef.current.animate(
      [{ transform: "scaleX(0)" }, { transform: "scaleX(1)" }],
      { duration: SLIDE_MS, easing: "linear", fill: "forwards" }
    );

    barAnimRef.current = anim;

    if (!animActive) {
      anim.pause();
      return;
    }

    slideTimerRef.current = setTimeout(nextImage, SLIDE_MS);
    startA11yTimer();

    return () => {
      clearSlideTimers();
      try {
        anim.cancel();
      } catch {}
      barAnimRef.current = null;
    };
  }, [currentIndex, animActive, nextImage]);

  useEffect(() => {
    const anim = barAnimRef.current;
    if (!anim) return;

    if (animActive) {
      const remaining = Math.max(0, SLIDE_MS - (anim.currentTime || 0));
      try {
        anim.play();
      } catch {}
      clearSlideTimers();
      slideTimerRef.current = setTimeout(nextImage, remaining);
      startA11yTimer();
    } else {
      try {
        anim.pause();
      } catch {}
      clearSlideTimers();
      const ct = anim.currentTime || 0;
      setProgressNow(Math.min(100, Math.round((ct / SLIDE_MS) * 100)));
    }
  }, [animActive, nextImage]);

  const handleGalleryKeys = (e) => {
    if (e.key === "ArrowLeft") prevImage();
    if (e.key === "ArrowRight") nextImage();
  };

  const handleEnter = () => {
    if (!galleryRef.current) return;
    galleryRef.current.style.setProperty("--scale", "1.012");
  };

  const handleLeave = () => {
    if (!galleryRef.current || !glowRef.current) return;
    galleryRef.current.style.setProperty("--rx", "0deg");
    galleryRef.current.style.setProperty("--ry", "0deg");
    galleryRef.current.style.setProperty("--scale", "1");
    glowRef.current.style.opacity = "0";
  };

  const handleMove = (e) => {
    if (!galleryRef.current || !glowRef.current || !imageRef.current) return;

    const galleryRect = galleryRef.current.getBoundingClientRect();
    const imageRect = imageRef.current.getBoundingClientRect();
    const lens = glowRef.current;
    const imgEl = imageRef.current;
    const zoom = 2.4;

    const clientX = e.clientX ?? 0;
    const clientY = e.clientY ?? 0;

    const x = Math.max(0, Math.min(galleryRect.width, clientX - galleryRect.left));
    const y = Math.max(0, Math.min(galleryRect.height, clientY - galleryRect.top));
    const nx = x / galleryRect.width - 0.5;
    const ny = y / galleryRect.height - 0.5;

    galleryRef.current.style.setProperty("--rx", `${ny * -5}deg`);
    galleryRef.current.style.setProperty("--ry", `${nx * 6}deg`);

    const boxW = imageRect.width;
    const boxH = imageRect.height;
    const naturalW = imgEl.naturalWidth || boxW;
    const naturalH = imgEl.naturalHeight || boxH;

    const imageRatio = naturalW / naturalH;
    const boxRatio = boxW / boxH;

    let renderedW;
    let renderedH;
    let offsetX;
    let offsetY;

    if (imageRatio > boxRatio) {
      renderedW = boxW;
      renderedH = boxW / imageRatio;
      offsetX = 0;
      offsetY = (boxH - renderedH) / 2;
    } else {
      renderedH = boxH;
      renderedW = boxH * imageRatio;
      offsetY = 0;
      offsetX = (boxW - renderedW) / 2;
    }

    const localX = clientX - imageRect.left;
    const localY = clientY - imageRect.top;

    const insidePaintedImage =
      localX >= offsetX &&
      localX <= offsetX + renderedW &&
      localY >= offsetY &&
      localY <= offsetY + renderedH;

    if (!insidePaintedImage) {
      lens.style.opacity = "0";
      return;
    }

    const paintedX = localX - offsetX;
    const paintedY = localY - offsetY;

    const lensW = lens.offsetWidth;
    const lensH = lens.offsetHeight;

    lens.style.opacity = "1";
    lens.style.left = `${x}px`;
    lens.style.top = `${y}px`;
    lens.style.transform = "translate(-50%, -50%)";
    lens.style.backgroundSize = `${renderedW * zoom}px ${renderedH * zoom}px`;
    lens.style.backgroundPosition = `${-(paintedX * zoom - lensW / 2)}px ${-(paintedY * zoom - lensH / 2)}px`;
  };

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    touchDeltaX.current = 0;
  };

  const onTouchMove = (e) => {
    if (touchStartX.current == null) return;
    touchDeltaX.current = e.touches[0].clientX - touchStartX.current;
  };

  const onTouchEnd = () => {
    const dx = touchDeltaX.current;
    touchStartX.current = null;
    touchDeltaX.current = 0;

    if (Math.abs(dx) > 30) {
      dx < 0 ? nextImage() : prevImage();
    }
  };

  const formatTime = (time) => {
    const m = Math.floor(time / 60);
    const s = time % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  return (
    <AnimatedBackground>
      <main className={styles.pageGrid}>
        <section
          className={styles.heroSection}
          aria-label={t("image_carousel") || "Image carousel"}
        >
          <div
            ref={galleryRef}
            className={cx(
              styles.galleryShell,
              styles.gradientFrame,
              styles.glassPanel,
              styles.tiltPanel,
              styles.neonFrame,
              !allowAnimate && styles.reducedMotion
            )}
            tabIndex={0}
            onKeyDown={handleGalleryKeys}
            onMouseEnter={handleEnter}
            onMouseLeave={handleLeave}
            onMouseMove={handleMove}
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
            role="region"
            aria-roledescription="carousel"
            aria-label={t("image_gallery") || "Featured images"}
          >
            <div
              ref={glowRef}
              className={styles.followGlow}
              aria-hidden="true"
              style={{ "--lens-image": `url(${IMAGES[currentIndex].src})` }}
            />
            <div className={styles.scanline} aria-hidden="true" />
            <div className={styles.gridOverlay} aria-hidden="true" />
            <div className={styles.cornerAccent} aria-hidden="true" />

            <button
              type="button"
              className={cx(styles.navArrow, styles.navArrowLeft)}
              aria-label={t("prev_image") || "Previous image"}
              title={t("previous") || "Previous"}
              onClick={prevImage}
            >
              &#10094;
            </button>

            <img
              ref={imageRef}
              key={IMAGES[currentIndex].src}
              src={IMAGES[currentIndex].src}
              alt={IMAGES[currentIndex].alt || `image${currentIndex + 1}`}
              className={cx(
                styles.galleryImage,
                styles.holoImage,
                !allowAnimate && styles.noKenburns
              )}
              loading="eager"
              decoding="async"
              fetchpriority="high"
            />

            <div className={styles.reflection} aria-hidden="true" />
            <div className={styles.aura} aria-hidden="true" />

            <button
              type="button"
              className={cx(styles.navArrow, styles.navArrowRight)}
              aria-label={t("next_image") || "Next image"}
              title={t("next") || "Next"}
              onClick={nextImage}
            >
              &#10095;
            </button>

            {allowAnimate && (
              <div
                className={styles.progressTrack}
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={progressNow}
                aria-label={t("slide_progress") || "Slide progress"}
              >
                <div ref={barRef} className={styles.progressBar} />
              </div>
            )}

            <div
              className={styles.dotNav}
              role="tablist"
              aria-label={t("image_navigation") || "Image navigation"}
            >
              {IMAGES.map((_, idx) => (
                <button
                  key={idx}
                  type="button"
                  role="tab"
                  aria-selected={idx === currentIndex}
                  className={cx(styles.dot, idx === currentIndex && styles.dotActive)}
                  onClick={() => setCurrentIndex(idx)}
                  title={`${t("image") || "Image"} ${idx + 1}`}
                />
              ))}
            </div>
          </div>

          <div className={styles.thumbRail} aria-label={t("thumbnails") || "Thumbnails"}>
            <div className={styles.thumbRow}>
              {THUMBS.map((image, index) => (
                <button
                  key={index}
                  type="button"
                  className={cx(styles.thumbButton, index === currentIndex && styles.thumbActive)}
                  onClick={() => setCurrentIndex(index)}
                  aria-label={`${t("open_image") || "Open image"}: ${image.alt}`}
                >
                  <img
                    src={image.thumb}
                    alt={image.alt}
                    className={styles.thumbImage}
                    loading="lazy"
                    decoding="async"
                  />
                </button>
              ))}
            </div>
          </div>

          <div
            className={styles.audioCta}
            role="group"
            aria-label={t("anthem_controls") || "Anthem controls"}
          >
            <button
              onClick={togglePlayPause}
              className={styles.anthemButton}
              aria-pressed={isPlaying}
              aria-label={
                isPlaying
                  ? t("stop_anthem") || "Stop anthem"
                  : t("play_anthem") || "Play anthem"
              }
            >
              {isPlaying ? t("stop") : t("anthem")}
            </button>

            <span className={styles.timecode} aria-live="polite">
              {formatTime(elapsedTime)} / {formatTime(totalDuration)}
            </span>

            <img
              src={rotarygloballogo}
              className={cx(styles.anthemLogo, isSpinning && styles.spinActive)}
              alt={t("rotary_logo_with_anthem") || "Rotary Club Logo spinning with anthem"}
            />
          </div>
        </section>

        <aside
          className={cx(styles.infoPanel, styles.glassPanel, styles.stickyPanel)}
          aria-labelledby="welcome-title"
        >
          <div className={styles.panelGlow} aria-hidden="true" />
          <div className={styles.panelGridOverlay} aria-hidden="true" />
          <div className={styles.panelCornerTop} aria-hidden="true" />
          <div className={styles.panelCornerBottom} aria-hidden="true" />

          <div className={styles.panelGrid}>
            <h2 id="welcome-title" className={styles.headline}>
              {t("welcome")}
            </h2>

            <p className={styles.introText}>{t("club_description")}</p>

            <section className={styles.infoBlock}>
              <div className={styles.infoBlockHeader}>
                <span className={styles.blockAccent} />
              </div>

              <div className={styles.leaders}>
                <p>{t("president")}</p>
                <p>{t("secretary")}</p>
              </div>
            </section>

            <section className={styles.infoBlock}>
              <div className={styles.infoBlockHeader}>
                <h4 className={styles.sectionTitle}>{t("latest_events")} 📅</h4>
              </div>

              <div className={styles.eventsList} role="list">
                <p role="listitem" className={styles.highlightedEvent}>
                  {t("events.1")}
                </p>
                <p role="listitem">{t("events.2")}</p>
                <p role="listitem">{t("events.3")}</p>
                <p role="listitem">{t("events.4")}</p>
                <p role="listitem">{t("events.5")}</p>
                <p role="listitem">{t("events.6")}</p>
                <p role="listitem">{t("events.7")}</p>
                <p role="listitem">{t("events.8")}</p>
                <p role="listitem">{t("events.9")}</p>
              </div>
            </section>

            <section className={styles.infoBlock}>
              <div className={styles.infoBlockHeader}>
                <h4 className={styles.sectionTitle}>{t("follow_us")}</h4>
              </div>

              <div className={styles.socialList}>
                <p className={styles.socialItem}>
                  <a
                    href="https://www.facebook.com/p/Rotary-Club-%C5%BDalec-100076549557937/?locale=sl_SI"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span>{t("facebook_link")}</span>
                    <span className={styles.socialDivider}>:</span>
                    <img src={Facebook} alt="" className={styles.socialIcon} />
                  </a>
                </p>

                <p className={styles.socialItem}>
                  <a
                    href="https://www.youtube.com/@RotaryClub%C5%BDalec"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span>{t("youtube_link")}</span>
                    <span className={styles.socialDivider}>:</span>
                    <img src={Youtube} alt="" className={styles.socialIcon} />
                  </a>
                </p>
              </div>
            </section>
          </div>
        </aside>
      </main>

      <section
        ref={revealRef}
        className={cx(styles.lowerSection, visible ? styles.sectionVisible : styles.sectionHidden)}
        aria-label={t("about_rotary") || "About Rotary"}
      >
        <div className={styles.timeline} aria-live="polite">
          <article className={cx(styles.timelineCard, styles.glassPanel)}>
            <div className={styles.timelineText}>
              <h3 className={styles.accentHeading}>{t("since")}</h3>
              <p className={styles.timelineParagraph}>{t("truth")}</p>
              <p className={styles.timelineParagraph}>{t("fair")}</p>
              <p className={styles.timelineParagraph}>{t("friendship")}</p>
              <p className={styles.timelineParagraph}>{t("beneficial")}</p>
            </div>

            <div className={styles.timelineMedia}>
              <img
                src="/Slike/Domov/rotaryslovenija.webp"
                alt="Rotary Slovenija Logo"
                style={{ height: "130px" }}
                loading="lazy"
                decoding="async"
              />
            </div>
          </article>

          <article className={cx(styles.timelineCard, styles.glassPanel)}>
            <div className={styles.timelineText}>
              <h3 className={styles.accentHeading}>{t("org_goal")}</h3>
              <p className={styles.timelineParagraph}>{t("org_goal_1")}</p>
              <p className={styles.timelineParagraph}>{t("org_goal_2")}</p>
            </div>

            <div className={styles.timelineMedia}>
              <img
                src="/Slike/Domov/k4.webp"
                alt="Rotarijska vprašanja"
                style={{ height: "150px" }}
                loading="lazy"
                decoding="async"
              />
            </div>
          </article>

          <article className={cx(styles.timelineCard, styles.glassPanel)}>
            <div className={styles.timelineText}>
              <h3 className={styles.accentHeading}>{t("why_member")}</h3>
              <p className={styles.timelineParagraph}>{t("why_friendship")}</p>
              <p className={styles.timelineParagraph}>{t("why_business")}</p>
              <p className={styles.timelineParagraph}>{t("why_leadership")}</p>
            </div>

            <div className={styles.timelineMedia}>
              <img
                src="/Slike/Domov/k3.webp"
                alt="Rotarijska vprašanja"
                className={styles.orbitImage}
                loading="lazy"
                decoding="async"
              />
            </div>
          </article>
        </div>
      </section>
    </AnimatedBackground>
  );
};

export default Domov;